import React, { PropsWithChildren, useContext, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import * as stripActions from '../actions/strip.actions';
import { stripByIdSelector } from '../selectors/strip.selectors';

const DEFAULT_LEADS: string[] = [];

interface Context {
  stripId: string;
}

export const StripContext = React.createContext<Context | null>(null);

type Props = PropsWithChildren<{
  id: string;
  duration: number; // ms
  leads?: string[];
  offset?: number; // ms
  durationOffset?: number; // ms
  mean?: number;
  onset?: number; // ms
}>;

export const Strip: React.FC<Props> = ({
  id,
  duration,
  leads = DEFAULT_LEADS,
  offset = 0,
  durationOffset = 0,
  onset = 0,
  children,
  mean = 0,
}) => {
  const dispatch = useDispatch();
  const strip = useSelector(stripByIdSelector(id));

  useEffect(() => {
    dispatch(
      stripActions.register({
        strip: {
          id,
          duration,
          leads,
          offset,
          gain: 1,
          mean,
          onset,
          durationOffset,
        },
        meta: { history: false },
      }),
    );
  }, [dispatch, id, duration, leads, offset, mean, onset, durationOffset]);

  useEffect(() => {
    return () => {
      dispatch(
        stripActions.unregister({
          stripId: id,
          meta: { history: false },
        }),
      );
    };
  }, [dispatch, id]);

  return strip == null ? (
    <></>
  ) : (
    <StripContext.Provider value={{ stripId: id }}>
      {children}
    </StripContext.Provider>
  );
};

export const useStripInfo = () => {
  const stripContext = useContext(StripContext);
  if (stripContext == null) {
    throw new Error('useStrip must be used inside <Strip />');
  }

  return stripContext;
};
